import AddressAppResource from './AddressAppResource';
import AppResource from '@milestoneslabs/milestones-js-engine/src/Data/AppResource';
import ChannelAppResource from './ChannelAppResource';
import ChannelMemberAppResource from './ChannelMemberAppResource';
import ContactAppResource from './ContactAppResource';
import EmailAppResource from './EmailAppResource';
import FileAppResource from './FileAppResource';
import OrganizationAppResource from './OrganizationAppResource';
import OrganizationMemberAppResource from './OrganizationMemberAppResource';
import PhoneAppResource from './PhoneAppResource';
import PropertyInstanceAppResource from './PropertyInstanceAppResource';
import RoleAppResource from './RoleAppResource';
import ServiceTicketAppResource from './ServiceTicketAppResource';
import WildAppResource from './WildAppResource';
import WorkspaceAppResource from './WorkspaceAppResource';
import WorkspaceOrganizationMemberAppResource from './WorkspaceOrganizationMemberAppResource';
import Engine from "@milestoneslabs/milestones-js-engine/src/Engine";
import MlsMembershipAppResource from "./MlsMembershipAppResource";
import MLSAppResource from "./MLSAppResource";
import ProfessionalLicenseAppResource from "./ProfessionalLicenseAppResource";
import LandingPageAppResource from "./LandingPageAppResource";
import EmailCampaignUserAppResource from "./EmailCampaignUserAppResource";
import UserNotificationAppResource from "./UserNotificationAppResource";
import ActivityHistorySavedViewAppResource from "./ActivityHistorySavedViewAppResource";
import UserAccountConfirmationAppResource from "./UserAccountConfirmationAppResource";
import TransactionAppResource from "./TransactionAppResource";
import LuPointOfOriginAppResource from "./LuPointOfOriginAppResource";
import ServiceAreaAppResource from "./ServiceAreaAppResource";

export const ACCOUNT_TYPE = {
    PROFESSIONAL: 'professional',
    CONSUMER: 'consumer',
}

export const USER_ROLES = {
    NORMAL_USER: 'normalUser',
    ADMIN: 'admin',
    ROOT: 'root',
}

class UserAppResource extends AppResource {

    /**
     * @returns {string}
     */
    appResourceType() {
        return 'User';
    }

    /**
     * @returns {string}
     */
    appResourceApiPrefix() {
        return 'users';
    }

    apiPaths() {
        return {
            ...super.apiPaths(),
            resetPassword: Engine.apiUrl(`${this.appResourceApiPrefix()}/reset-password`),
            setPassword: Engine.apiUrl(`${this.appResourceApiPrefix()}/{uuid}/set-password`),
            affiliates: Engine.apiUrl(`${this.appResourceApiPrefix()}/sales/rep/affiliates`),
            overallStats: Engine.apiUrl(`${this.appResourceApiPrefix()}/sales/rep/overall-stats`),
            affiliatesOnboarding: Engine.apiUrl(`${this.appResourceApiPrefix()}/sales/rep/affiliates/onboarding`),
            generatePersonalAccessToken: Engine.apiUrl(`${this.appResourceApiPrefix()}/generate-personal-access-token`),
            getUpgradeExperiencePlans: Engine.apiUrl(`${this.appResourceApiPrefix()}/upgrade-experience-plans`),
        };
    }

    /**
     * @returns {*[]}
     */
    hidden() {
        return super.hidden().concat([
            'uuid',
            'email_verified_at',
            'deleted_at',
        ]);
    }

    /**
     * @returns {*[]}
     */
    fillable() {
        return [
            'rememberToken',
            'username',
            'phone',
            'email',
            'firstName',
            'middleName',
            'lastName',
            'fullName',
            'displayName',
            'password',
            'passwordConfirmation',
            'avatarFileUuid',
            'organizationLogoUuid',
            'lastWorkspaceUuid',
            'genderType',
            'status',
            'meta',
            'timezone',
            'applicationState',
            'onboardingState',
            'roles',
            'ownedOrganizations',
            'organizations',
            'organizationMemberships',
            'organizationMembershipsWithTrashed',
            'ownedWorkspaces',
            'workspaces',
            'workspaceMemberships',
            'avatarFile',
            'organizationLogo',
            'properties',
            'files',
            'messages',
            'channels',
            'channelMembers',
            'contacts',
            'emails',
            'phones',
            'addresses',
            'rolesMeta',
            'emailsMeta',
            'phonesMeta',
            'addressesMeta',
            'notifications',
            'landingPageProjects',
            'nylasToken',
            'nylasEmail',
            'nylasLoginSync',
            'moxiAgentUuid',
            'moxiAgentId',
            'moxiContactsSync',
            'moxiTitle',
            'moxiAccessLevel',
            'firstTimeFileUpload',
            'pointOfOrigin',
            'platformBrandingOrganization',
            'platformBrandingOrganizationUuid',
            'isNylasTokenValid',
            'serviceAreas',
        ];
    }

    /**
     * @returns {*[]}
     */
    relationMap() {
        return {
            roles: RoleAppResource,
            ownedOrganizations: OrganizationAppResource,
            organizations: OrganizationAppResource,
            organizationMemberships: OrganizationMemberAppResource,
            organizationMembershipsWithTrashed: OrganizationMemberAppResource,
            ownedWorkspaces: WorkspaceAppResource,
            workspaces: WorkspaceAppResource,
            workspaceMemberships: WorkspaceOrganizationMemberAppResource,
            avatarFile: FileAppResource,
            organizationLogo: FileAppResource,
            properties: PropertyInstanceAppResource,
            files: FileAppResource,
            messages: WildAppResource,
            channels: ChannelAppResource,
            channelMembers: ChannelMemberAppResource,
            contacts: ContactAppResource,
            emails: EmailAppResource,
            phones: PhoneAppResource,
            addresses: AddressAppResource,
            serviceTickets: ServiceTicketAppResource,
            mlsMemberships: MlsMembershipAppResource,
            mls: MLSAppResource,
            licenses: ProfessionalLicenseAppResource,
            landingPageProjects: WorkspaceAppResource,
            landingPages: LandingPageAppResource,
            emailCampaignUser: EmailCampaignUserAppResource,
            relationNotifications: UserNotificationAppResource,
            savedViews: ActivityHistorySavedViewAppResource,
            accountConfirmations: UserAccountConfirmationAppResource,
            userTransactions: TransactionAppResource,
            pointOfOrigin: LuPointOfOriginAppResource,
            platformBrandingOrganization: OrganizationAppResource,
            loanOfficerLicense: ProfessionalLicenseAppResource,
            serviceAreas: ServiceAreaAppResource,
        };
    }

    /**
     * @returns {*[]}
     */
    relationForeignKeys() {
        return {
            avatarFile: 'avatar_file_uuid',
            organizationLogo: 'organization_logo_uuid',
            platformBrandingOrganization: 'platform_branding_organization_uuid',
        };
    }

    /**
     * @returns {*}
     */
    validationRules() {
        return {
            phone: {
                type: 'string',
                length: {
                    maximum: 255,
                },
            },
            email: {
                type: 'string',
                presence: true,
                email: true,
                length: {
                    maximum: 255,
                },
            },
            firstName: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
                includesLetter: true,
            },
            middleName: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            lastName: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
                includesLetter: true,
            },
            fullName: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            displayName: {
                type: 'string',
                length: {
                    minimum: 2,
                    maximum: 255,
                },
            },
            password: {
                type: 'string',
                presence: !this.exists,
                confirmed: true,
                length: {
                    minimum: 8,
                    maximum: 255,
                },
            },
            passwordConfirmation: {
                type: 'string',
                presence: !this.exists,
            },
            genderType: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
                inclusion: [
                    'male',
                    'female',
                ],
            },
            avatarFileUuid: {
                type: 'string',
            },
            organizationLogoUuid: {
                type: 'string',
            },
            lastWorkspaceUuid: {
                type: 'string',
            },
            roles: {
                array: {
                    items: {
                        type: 'string',
                        inclusion: [
                            'admin',
                            'normalUser',
                            'root',
                        ],
                    },
                },
            },
            meta: {},
            timezone: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            applicationState: {},
            onboardingState: {},
            rolesMeta: {
                array: {
                    items: {
                        array: {
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
            emails: {
                array: {
                    items: {
                        email: true
                    },
                },
            },
            emailsMeta: {
                array: {
                    items: {
                        array: {
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
            phones: {
                array: {
                    items: {
                        type: 'string',
                    },
                },
            },
            phonesMeta: {
                array: {
                    items: {
                        array: {
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
            addresses: {
                array: {
                    items: {
                        type: 'string',
                    },
                },
            },
            addressesMeta: {
                array: {
                    items: {
                        array: {
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
            },
            notifications: {},
            nylasToken: {
                type: 'string',
                length: {
                    minimum: 1,
                    maximum: 255,
                },
            },
            nylasEmail: {
                type: 'string',
            },
            moxiAgentUuid: {
                type: 'string',
            },
            moxiAgentId: {
                type: 'string',
            },
            moxiContactsSync: {
                type: 'boolean',
            },
            moxiTitle: {
                type: 'string',
            },
            moxiAccessLevel: {
                type: 'string',
            },
            firstTimeFileUpload: {
                type: 'boolean',
            },
            platformBrandingOrganizationUuid: {
                type: 'string',
            },
            isNylasTokenValid: {
                type: 'boolean',
            }
        };
    }

    async resetUserPassword(data) {
        if (typeof data !== 'object') {
            throw new Error('data must be an object');
        }
        if (!data.organizationUuid) {
            throw new Error('organizationUuid is required property for data');
        }
        if (!data.userUuid) {
            if (!this._uuid) {
                throw new Error('userUuid is required property for data');
            }
            data.userUuid = this._uuid;
        }

        const apiResponse = await this.enginePost(this.apiPaths().resetPassword, data);

        return apiResponse;
    }

    async setUserPassword(data) {
        if (!this.uuid) {
            return Promise.resolve(null);
        }

        const apiResponse = await this.enginePost(this.apiPaths().setPassword.replace('{uuid}', this.uuid), data);

        return apiResponse;
    }

    async overallStats(data) {
        return await this.enginePost(this.apiPaths().overallStats, data);
    }

    async affiliates(data) {
        return await this.enginePost(this.apiPaths().affiliates, data);
    }

    async affiliatesOnboarding(data) {
        return await this.enginePost(this.apiPaths().affiliatesOnboarding, data);
    }

    async getUpgradeExperiencePlans() {
        const url = this.apiPaths().getUpgradeExperiencePlans;

        const apiResponse = await Engine.get(url);

        return apiResponse?.rawData;
    }

    async generatePersonalAccessToken(name) {
        return await this.enginePost(
            this.apiPaths().generatePersonalAccessToken,
            {
                name: name
            }
        );
    }
}

export default UserAppResource;
